import React, { useState, useEffect, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import DOMPurify from 'dompurify';
import { 
  Plus, 
  Sparkles, 
  FileText, 
  Eye, 
  EyeOff, 
  Trash2, 
  Loader2,
  CheckCircle2,
  AlertCircle,
  ExternalLink
} from 'lucide-react';
import Header from '../components/layout/Header';
import Footer from '../components/layout/Footer';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { toast } from 'sonner';
import { useAuth } from '../context/AuthContext';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const BlogAdminPage = () => {
  const { token } = useAuth();
  const navigate = useNavigate();
  const [topics, setTopics] = useState([]);
  const [articles, setArticles] = useState([]);
  const [selectedTopic, setSelectedTopic] = useState('');
  const [customTitle, setCustomTitle] = useState('');
  const [isGenerating, setIsGenerating] = useState(false);
  const [generatedBlog, setGeneratedBlog] = useState(null);
  const [loading, setLoading] = useState(true);

  // Redirect if not authenticated
  useEffect(() => {
    if (!token) {
      navigate('/login');
      toast.error('Bitte melden Sie sich an');
    }
  }, [token, navigate]);

  // Fetch topics and articles
  const fetchData = useCallback(async () => {
    try {
      const [topicsRes, articlesRes] = await Promise.all([
        fetch(`${API_URL}/api/blog/topics`),
        fetch(`${API_URL}/api/blog/articles?published_only=false`)
      ]);
      
      const topicsData = await topicsRes.json();
      const articlesData = await articlesRes.json();
      
      setTopics(topicsData.topics || []);
      setArticles(articlesData.articles || []);
    } catch (error) {
      toast.error('Fehler beim Laden der Daten');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Generate blog article
  const handleGenerate = async () => {
    if (!selectedTopic || !customTitle.trim()) {
      toast.error('Bitte Thema und Titel auswählen');
      return;
    }

    setIsGenerating(true);
    setGeneratedBlog(null);

    try {
      const response = await fetch(`${API_URL}/api/blog/generate`, {
        method: 'POST',
        headers: { 
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          topic_id: selectedTopic,
          custom_title: customTitle
        })
      });

      if (response.status === 401) {
        toast.error('Sitzung abgelaufen. Bitte erneut anmelden.');
        navigate('/login');
        return;
      }

      if (!response.ok) {
        throw new Error('Fehler bei der Generierung');
      }

      const data = await response.json();
      setGeneratedBlog(data.blog);
      setArticles(prev => [data.blog, ...prev]);
      toast.success('Blog-Artikel wurde erstellt!');
      
      // Reset form
      setCustomTitle('');
      
    } catch (error) {
      toast.error('Fehler bei der Blog-Generierung');
    } finally {
      setIsGenerating(false);
    }
  };

  // Publish/unpublish article
  const handlePublish = async (blogId, publish) => {
    try {
      const response = await fetch(`${API_URL}/api/blog/publish`, {
        method: 'POST',
        headers: { 
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          blog_id: blogId,
          is_published: publish
        })
      });

      if (response.status === 401) {
        toast.error('Sitzung abgelaufen. Bitte erneut anmelden.');
        navigate('/login');
        return;
      }

      if (!response.ok) throw new Error();

      setArticles(prev => prev.map(a => 
        a.id === blogId ? { ...a, is_published: publish } : a
      ));
      
      toast.success(publish ? 'Artikel veröffentlicht' : 'Artikel zurückgezogen');
    } catch {
      toast.error('Fehler beim Aktualisieren');
    }
  };

  // Delete article
  const handleDelete = async (blogId) => {
    if (!window.confirm('Artikel wirklich löschen?')) return;

    try {
      const response = await fetch(`${API_URL}/api/blog/article/${blogId}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (response.status === 401) {
        toast.error('Sitzung abgelaufen. Bitte erneut anmelden.');
        navigate('/login');
        return;
      }

      if (!response.ok) throw new Error();

      setArticles(prev => prev.filter(a => a.id !== blogId));
      toast.success('Artikel gelöscht');
    } catch {
      toast.error('Fehler beim Löschen');
    }
  };

  const selectedTopicData = topics.find(t => t.id === selectedTopic);

  return (
    <div className="min-h-screen bg-[#f8f9fa]">
      <Header />
      
      <main className="pt-24 pb-16">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          
          {/* Header */}
          <div className="mb-8">
            <h1 className="text-3xl font-bold text-[#1a1c1b] mb-2">Blog-Generator</h1>
            <p className="text-[#5a5d5b]">
              Erstellen Sie SEO-optimierte Blog-Artikel mit KI-Unterstützung
            </p>
          </div>

          <div className="grid lg:grid-cols-3 gap-8">
            
            {/* Generator Panel */}
            <div className="lg:col-span-1">
              <div className="bg-white rounded-2xl shadow-lg p-6 sticky top-24">
                <h2 className="text-lg font-semibold text-[#1a1c1b] mb-6 flex items-center gap-2">
                  <Sparkles className="text-[#FF0000]" size={20} />
                  Neuen Artikel erstellen
                </h2>

                <div className="space-y-5">
                  {/* Topic Selection */}
                  <div>
                    <Label htmlFor="topic">SEO-Thema wählen *</Label>
                    <Select value={selectedTopic} onValueChange={setSelectedTopic}>
                      <SelectTrigger className="mt-1 bg-white" data-testid="select-topic">
                        <SelectValue placeholder="Thema auswählen..." />
                      </SelectTrigger>
                      <SelectContent>
                        {topics.map(topic => (
                          <SelectItem key={topic.id} value={topic.id}>
                            {topic.title}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    
                    {selectedTopicData && (
                      <div className="mt-3 p-3 bg-[#f8f9fa] rounded-lg">
                        <p className="text-sm text-[#5a5d5b] mb-2">{selectedTopicData.description}</p>
                        <div className="flex flex-wrap gap-1">
                          {selectedTopicData.keywords.map((kw) => (
                            <span key={kw} className="text-xs px-2 py-0.5 bg-[#FFE5E5] text-[#CC0000] rounded">
                              {kw}
                            </span>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>

                  {/* Custom Title */}
                  <div>
                    <Label htmlFor="title">Artikel-Titel *</Label>
                    <Input
                      id="title"
                      value={customTitle}
                      onChange={(e) => setCustomTitle(e.target.value)}
                      placeholder="z.B. Unternehmensbewertung 2025: Was KMU wissen müssen"
                      className="mt-1 bg-white"
                      data-testid="input-title"
                    />
                    <p className="text-xs text-[#888b89] mt-1">
                      Verwenden Sie relevante Keywords im Titel
                    </p>
                  </div>

                  {/* Generate Button */}
                  <Button
                    onClick={handleGenerate}
                    disabled={isGenerating || !selectedTopic || !customTitle.trim()}
                    className="w-full bg-[#FF0000] hover:bg-[#CC0000] text-white"
                    data-testid="btn-generate"
                  >
                    {isGenerating ? (
                      <>
                        <Loader2 size={18} className="mr-2 animate-spin" />
                        Wird generiert...
                      </>
                    ) : (
                      <>
                        <Plus size={18} className="mr-2" />
                        Artikel generieren
                      </>
                    )}
                  </Button>

                  {isGenerating && (
                    <p className="text-sm text-center text-[#5a5d5b]">
                      Die KI erstellt Ihren Artikel mit 5 Quellen...
                      <br />
                      Dies kann bis zu 30 Sekunden dauern.
                    </p>
                  )}
                </div>

                {/* Info Box */}
                <div className="mt-6 p-4 bg-blue-50 rounded-xl">
                  <h3 className="font-medium text-blue-900 text-sm mb-2">Artikelspezifikationen</h3>
                  <ul className="text-xs text-blue-800 space-y-1">
                    <li>• 600-800 Wörter</li>
                    <li>• H1, H2, H3 Struktur</li>
                    <li>• 5 verifizierte Quellen</li>
                    <li>• Blocksatz-Format</li>
                    <li>• SEO-optimiert</li>
                  </ul>
                </div>
              </div>
            </div>

            {/* Articles List */}
            <div className="lg:col-span-2">
              <div className="bg-white rounded-2xl shadow-lg p-6">
                <h2 className="text-lg font-semibold text-[#1a1c1b] mb-6 flex items-center gap-2">
                  <FileText className="text-[#5a5d5b]" size={20} />
                  Alle Artikel ({articles.length})
                </h2>

                {loading ? (
                  <div className="py-12 text-center">
                    <Loader2 className="animate-spin mx-auto text-[#FF0000]" size={32} />
                  </div>
                ) : articles.length === 0 ? (
                  <div className="py-12 text-center text-[#5a5d5b]">
                    <FileText size={48} className="mx-auto mb-4 opacity-30" />
                    <p>Noch keine Artikel erstellt.</p>
                    <p className="text-sm">Generieren Sie Ihren ersten Blog-Artikel!</p>
                  </div>
                ) : (
                  <div className="space-y-4">
                    {articles.map(article => (
                      <div 
                        key={article.id}
                        className={`p-4 rounded-xl border transition-colors ${
                          article.is_published 
                            ? 'border-green-200 bg-green-50/30' 
                            : 'border-gray-200 bg-gray-50/30'
                        }`}
                      >
                        <div className="flex items-start justify-between gap-4">
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2 mb-1">
                              {article.is_published ? (
                                <span className="flex items-center gap-1 text-xs text-green-600 font-medium">
                                  <CheckCircle2 size={14} />
                                  Veröffentlicht
                                </span>
                              ) : (
                                <span className="flex items-center gap-1 text-xs text-amber-600 font-medium">
                                  <AlertCircle size={14} />
                                  Entwurf
                                </span>
                              )}
                              <span className="text-xs text-[#888b89]">
                                {new Date(article.created_at).toLocaleDateString('de-CH')}
                              </span>
                            </div>
                            
                            <h3 className="font-semibold text-[#1a1c1b] truncate">
                              {article.title}
                            </h3>
                            
                            <p className="text-sm text-[#5a5d5b] mt-1 line-clamp-2">
                              {article.meta_description}
                            </p>
                            
                            <div className="flex items-center gap-3 mt-2 text-xs text-[#888b89]">
                              <span>{article.category}</span>
                              <span>•</span>
                              <span>{article.word_count} Wörter</span>
                              <span>•</span>
                              <span>{article.sources?.length || 0} Quellen</span>
                            </div>
                          </div>

                          <div className="flex items-center gap-2 flex-shrink-0">
                            {article.is_published && (
                              <Link
                                to={`/blog/${article.slug}`}
                                target="_blank"
                                className="p-2 text-[#5a5d5b] hover:text-[#FF0000] transition-colors"
                                title="Ansehen"
                              >
                                <ExternalLink size={18} />
                              </Link>
                            )}
                            
                            <button
                              onClick={() => handlePublish(article.id, !article.is_published)}
                              className={`p-2 transition-colors ${
                                article.is_published 
                                  ? 'text-green-600 hover:text-amber-600' 
                                  : 'text-[#5a5d5b] hover:text-green-600'
                              }`}
                              title={article.is_published ? 'Zurückziehen' : 'Veröffentlichen'}
                            >
                              {article.is_published ? <EyeOff size={18} /> : <Eye size={18} />}
                            </button>
                            
                            <button
                              onClick={() => handleDelete(article.id)}
                              className="p-2 text-[#5a5d5b] hover:text-red-600 transition-colors"
                              title="Löschen"
                            >
                              <Trash2 size={18} />
                            </button>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* Preview of Generated Blog */}
              {generatedBlog && (
                <div className="mt-8 bg-white rounded-2xl shadow-lg p-6">
                  <h2 className="text-lg font-semibold text-[#1a1c1b] mb-4 flex items-center gap-2">
                    <CheckCircle2 className="text-green-500" size={20} />
                    Neu generierter Artikel
                  </h2>
                  
                  <div className="border rounded-xl p-6 bg-gray-50">
                    <div 
                      className="prose prose-sm max-w-none
                        prose-headings:text-[#1a1c1b]
                        prose-h1:text-2xl prose-h1:mb-4
                        prose-h2:text-xl prose-h2:text-[#FF0000] prose-h2:mt-6
                        prose-h3:text-lg prose-h3:mt-4
                        prose-p:text-[#333] prose-p:text-justify prose-p:mb-3"
                      dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(generatedBlog.content_html) }}
                    />
                    
                    {generatedBlog.sources && (
                      <div className="mt-6 pt-4 border-t">
                        <h4 className="font-semibold text-sm mb-3">Quellen:</h4>
                        <ol className="text-sm space-y-2">
                          {generatedBlog.sources.map((s) => (
                            <li key={s.url || s.title} className="flex items-start gap-2">
                              <span className="text-[#FF0000] font-medium">{generatedBlog.sources.indexOf(s) + 1}.</span>
                              <a href={s.url} target="_blank" rel="noopener noreferrer" className="text-[#FF0000] hover:underline">
                                {s.title}
                              </a>
                            </li>
                          ))}
                        </ol>
                      </div>
                    )}
                  </div>
                  
                  <div className="mt-4 flex gap-3">
                    <Button
                      onClick={() => handlePublish(generatedBlog.id, true)}
                      className="bg-[#FF0000] hover:bg-[#CC0000] text-white"
                    >
                      <Eye size={18} className="mr-2" />
                      Jetzt veröffentlichen
                    </Button>
                    <Button
                      onClick={() => setGeneratedBlog(null)}
                      variant="outline"
                    >
                      Vorschau schliessen
                    </Button>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </main>
      
      <Footer />
    </div>
  );
};

export default BlogAdminPage;
